import {
    IChanges,
    IDictFilters,
    IDocumentTable,
    IExternalDataSource,
    IFormValues,
    IGridModalAttributes,
    ISetValues,
    IStyles,
    TableColumn,
} from '@models/Forms/IForms';
import { collectTableColumns } from '@molecules/formbuilder/controls/Table/controls/tableColumns';
import { formDataRowSets } from '@molecules/formbuilder/controls/Table/controls/formDataRowSets';
import { ITableSchemeColumn, ITableSchemeDebug, ITableSchemeDetail } from './tableSchemeDebug';

const COLUMN_SCALARS = [
    'id',
    'valueType',
    'width',
    'order',
    'alignment',
    'hidden',
    'readonly',
    'required',
    'requiredMessage',
    'visibilityRules',
    'min',
    'max',
    'pattern',
    'maxLength',
    'title',
    'viewTitle',
    'placeholder',
    'formula',
    'dictName',
    'displayFormat',
    'isMultiple',
    'template',
    'href',
    'floatPoints',
    'trueText',
    'falseText',
    'loadMode',
    'isSelectMode',
    'modalTitle',
    'startLevel',
    'selectableLevels',
    'visibleLevels',
    'isFormData',
    'total',
    'workCalendarRules',
    'inputRegExp',
    'isValue',
] as const;

function text(value: unknown): string | undefined {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || undefined;
    }
    if (typeof value === 'boolean') return value ? 'да' : 'нет';
    if (typeof value === 'number') return String(value);
    return undefined;
}

function scalars(source: object, keys: readonly string[]): ITableSchemeDetail[] {
    const record = source as Record<string, unknown>;
    return keys.flatMap((key) => {
        if (!(key in record)) return [];
        const value = text(record[key]);
        return value === undefined ? [] : [{ label: key, value }];
    });
}

function group(label: string, children: Array<ITableSchemeDetail | undefined>): ITableSchemeDetail | undefined {
    const items = children.filter((item): item is ITableSchemeDetail => !!item);
    if (items.length === 0) return undefined;
    return { label, value: String(items.length), children: items };
}

function setValuesNode(label: string, values: ISetValues | undefined): ITableSchemeDetail | undefined {
    if (!values) return undefined;
    const children: ITableSchemeDetail[] = [];
    for (const set of values.sets ?? []) {
        const formula = set.attr?.trim() || '';
        const condition = set.setCondition?.trim() || '';
        const hint = set.type?.trim() || undefined;
        if (!set.key && !formula && !condition && !hint) continue;
        children.push({
            label: set.key || 'set',
            value: formula,
            hint,
            children: condition ? [{ label: 'если', value: condition }] : undefined,
        });
    }
    for (const row of values.addRows ?? []) {
        children.push({
            label: 'addRow',
            value: [row.table, row.subTable, row.countRows].filter((bit) => !!bit?.trim()).join(' / '),
        });
    }
    for (const row of values.updateRows ?? []) {
        children.push({
            label: 'updateRow',
            value: [row.table, row.subTable, row.filter].filter((bit) => !!bit?.trim()).join(' / '),
        });
    }
    for (const row of values.deleteRows ?? []) {
        children.push({
            label: 'deleteRow',
            value: [row.table, row.subTable, row.filter].filter((bit) => !!bit?.trim()).join(' / '),
        });
    }
    for (const row of values.clearTables ?? []) {
        children.push({
            label: 'clearTable',
            value: [row.table, row.subTable, row.column].filter((bit) => !!bit?.trim()).join(' / '),
        });
    }
    for (const proc of values.sqlProcedures ?? []) {
        if (proc.name) children.push({ label: 'sql', value: proc.name });
    }
    return group(label, children);
}

function changesNode(changes: IChanges | undefined, depth = 0): ITableSchemeDetail | undefined {
    const list = changes?.change ?? [];
    if (list.length === 0 || depth > 4) return undefined;
    const children = list.flatMap((change, index) => {
        const nested = [
            setValuesNode('setValues', change.setValues),
            change.showErrors?.messages?.length
                ? { label: 'showErrors', value: String(change.showErrors.messages.length) }
                : undefined,
            changesNode(change.elseChanges?.changes, depth + 1),
            changesNode(change.changes, depth + 1),
        ].filter((item): item is ITableSchemeDetail => !!item);
        const condition = change.condition?.trim() || '';
        if (!condition && nested.length === 0) return [];
        return [{ label: `#${index + 1}`, value: condition, children: nested }];
    });
    return children.length === 0 ? undefined : { label: 'changes', value: String(children.length), children };
}

function filtersNode(filters: IDictFilters | undefined): ITableSchemeDetail | undefined {
    const list = filters?.filter ?? [];
    if (list.length === 0) return undefined;
    return group(
        'filters',
        list.map((filter) => {
            const condition = filter.condition?.trim() || '';
            return {
                label: 'фильтр',
                value: filter.filter || filter.script || '',
                children: condition ? [{ label: 'если', value: condition }] : undefined,
            };
        }),
    );
}

function stylesNode(styles: IStyles | undefined): ITableSchemeDetail | undefined {
    const list = styles?.style ?? [];
    if (list.length === 0) return undefined;
    return group(
        'styles',
        list.map((style) => ({
            label: 'стиль',
            value: style.condition?.trim() || '',
            hint: style.cellStyle?.trim() || undefined,
        })),
    );
}

function gridNode(grid: IGridModalAttributes | undefined): ITableSchemeDetail | undefined {
    const attrs = grid?.attrs ?? [];
    if (attrs.length === 0) return undefined;
    return group(
        'gridAttribute',
        attrs.map((attr) => ({
            label: attr.key || attr.name,
            value: [attr.name, attr.type].filter((bit) => !!bit).join(' · '),
        })),
    );
}

function formValuesNode(values: IFormValues | undefined): ITableSchemeDetail | undefined {
    const list = values?.formValue ?? [];
    if (list.length === 0) return undefined;
    return group(
        'formValues',
        list.map((value) => ({
            label: value.key || value.displayName || 'formValue',
            value: value.formula || value.attr || value.column || '',
        })),
    );
}

function externalNode(source: IExternalDataSource | undefined): ITableSchemeDetail | undefined {
    if (!source) return undefined;
    return group('externalDataSource', [
        source.tableSource?.tableName
            ? {
                  label: 'table',
                  value: [source.tableSource.tableName, source.tableSource.subTableName].filter((bit) => !!bit).join(' / '),
              }
            : undefined,
        source.formDataSource?.dictName ? { label: 'dict', value: source.formDataSource.dictName } : undefined,
    ]);
}

function columnAttributes(column: TableColumn, kind: string): ITableSchemeDetail[] {
    const record = column as TableColumn & {
        changes?: IChanges;
        setValues?: ISetValues;
        filters?: IDictFilters;
        styles?: IStyles;
        gridAttribute?: IGridModalAttributes;
        formValues?: IFormValues;
        externalDataSource?: IExternalDataSource;
    };
    return [
        { label: 'вид', value: kind },
        ...scalars(column, COLUMN_SCALARS).filter((item) => item.label !== 'valueType' || kind !== 'field'),
        changesNode(record.changes),
        setValuesNode('setValues', record.setValues),
        filtersNode(record.filters),
        stylesNode(record.styles),
        gridNode(record.gridAttribute),
        formValuesNode(record.formValues),
        externalNode(record.externalDataSource),
    ].filter((item): item is ITableSchemeDetail => !!item);
}

export interface IDocumentFormTableScheme {
    debug: ITableSchemeDebug;
    columns: ITableSchemeColumn[];
    details: ITableSchemeDetail[];
    nested: ITableSchemeDetail[];
}

export function documentFormTableScheme(table: IDocumentTable): IDocumentFormTableScheme {
    const columns = collectTableColumns(table).map((source) => {
        const column = source.column as TableColumn & { name?: string; valueType?: string };
        const valueType = column.valueType ? String(column.valueType) : undefined;
        return {
            header: column.name || column.key,
            field: column.key,
            dataType: source.kind === 'field' ? valueType : source.kind,
            attributes: columnAttributes(source.column, source.kind),
        };
    });

    const details = scalars(table, ['id', 'pageSize', 'allowFiltersRow', 'showRowForm', 'viewInForm', 'allowFullscreen', 'allowColumnChooser', 'rowDragging', 'allowExport']);

    const nested = [
        group(
            'правила',
            scalars(table, [
                'addRowButtonRules',
                'removeRowButtonRules',
                'singleRowDeleteButtonRules',
                'copyRowButtonRules',
                'singleRowCopyButtonRules',
                'editInFormFormula',
                'captionFormula',
            ]),
        ),
        group(
            'подтаблицы',
            (table.tables ?? []).map((child) => ({
                label: child.name || child.key,
                value: child.key,
                children: child.visibilityRules ? [{ label: 'visibilityRules', value: child.visibilityRules }] : undefined,
            })),
        ),
        group(
            'columnSets',
            (table.columnSets ?? []).map((set) => ({
                label: set.name || set.id,
                value: (set.columns ?? []).join(', '),
            })),
        ),
        changesNode(table.changes),
        setValuesNode('newRowData', table.newRowData?.setValues),
        setValuesNode('copyRowData', table.copyRowData?.setValues),
        ...formDataRowSets(table).map(({ key, config }, index, sets) =>
            group(sets.length > 1 ? `addFormDataRows ${config.id || index + 1}` : 'addFormDataRows', [
                { label: 'name', value: config.name || '' },
                config.id ? { label: 'id', value: key } : undefined,
                config.visibilityRules ? { label: 'visibilityRules', value: config.visibilityRules } : undefined,
                config.addRowButtonRules ? { label: 'addRowButtonRules', value: config.addRowButtonRules } : undefined,
            ]),
        ),
        table.extendsDataSource?.sqlProcedure?.name
            ? { label: 'extendsDataSource', value: table.extendsDataSource.sqlProcedure.name }
            : undefined,
    ].filter((item): item is ITableSchemeDetail => !!item);

    return {
        debug: {
            title: table.name || table.key,
            tableKey: table.key,
            visibilityRules: table.visibilityRules ?? '',
            sourcePath: `форма · ${table.key}`,
        },
        columns,
        details,
        nested,
    };
}
